const MONTH = { month: 'short', year: 'numeric' }

export const monthLabel = ym => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { ...MONTH, timeZone: 'UTC' })
}

export const money = cents =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const PACE_OPTIONS = [
  { name: 'Full: up to three plays a month', value: 'full' },
  { name: 'Light: one play a month', value: 'light' },
  { name: 'Paused', value: 'paused' }
]

export const BUDGET_OPTIONS = [
  { name: 'Under $100 a month', value: '$' },
  { name: 'Up to $500 a month', value: '$$' },
  { name: 'More than $500 a month', value: '$$$' }
]

const PALETTE = ['#0f6b5c', '#e8a317', '#7a5ea8', '#2f6fb3', '#c0504d', '#6b7470']
const DAY = '#0f6b5c'
const SEND_DAY_BAR = '#111827'
const DAY_BAR = '#d1d5db'

export function resolvePlans(json) {
  const included = json.included || []
  const find = ref => included.find(r => r.type === ref.type && r.id === ref.id)
  return (json.data || []).map(plan => ({
    ...plan,
    items: (plan.relationships?.marketing_plan_items?.data || [])
      .map(find)
      .filter(Boolean)
      .map(item => ({ ...item, play: find(item.relationships?.marketing_play?.data || {}) || null }))
      .sort((a, b) => a.attributes.position - b.attributes.position)
  }))
}

export function ordersByPlayChart(payload) {
  return {
    labels: payload.months.map(monthLabel),
    datasets: payload.series.map((s, i) => ({
      label: s.play,
      data: s.values,
      backgroundColor: PALETTE[i % PALETTE.length]
    }))
  }
}

export function ordersByDayChart(payload) {
  const sendDays = new Set(payload?.send_days || [])
  const days = payload?.days || []
  return {
    labels: days.map(d => d.slice(5)),
    datasets: [
      {
        label: 'Orders',
        data: payload?.values || [],
        backgroundColor: days.map(d => (sendDays.has(d) ? SEND_DAY_BAR : DAY_BAR))
      }
    ]
  }
}

export function aiVisibilityChart(payload) {
  return {
    labels: payload.months.map(monthLabel),
    datasets: [
      {
        label: 'Named in AI answers (%)',
        data: payload.values.map(v => (v === null || v === undefined ? null : Math.round(v * 100))),
        borderColor: DAY,
        backgroundColor: DAY,
        spanGaps: true
      }
    ]
  }
}

export function funnelChart(payload) {
  const stages = [
    ['Proposed', 'proposed'],
    ['Approved', 'approved'],
    ['Sent', 'sent'],
    ['Ordered', 'ordered']
  ]
  return {
    labels: payload.months.map(monthLabel),
    datasets: stages.map(([label, key], i) => ({ label, data: payload[key], backgroundColor: PALETTE[i] }))
  }
}

export function tileList(tiles) {
  const list = [
    { label: 'Plays run', value: String(tiles.plays_run || 0) },
    { label: 'Orders attributed', value: String(tiles.orders || 0) },
    { label: 'Revenue attributed', value: money(tiles.revenue_cents) },
    { label: 'AI spend on drafts', value: money(tiles.ai_spend_cents) }
  ]
  if (tiles.bottle_fee_cents !== undefined) list.push({ label: 'Bottle fee', value: money(tiles.bottle_fee_cents) })
  return list
}

export const PACE_CARDS = [
  { value: 'full', title: 'Full', text: 'Up to 3 plays a month. One online, one offline, one for regulars.' },
  { value: 'light', title: 'Light', text: '2 plays a month. Good when you are stretched.' },
  { value: 'paused', title: 'Paused', text: 'Nothing new until you come back.' }
]

export const BUDGET_CARDS = [
  { value: '$', title: 'Free only', text: 'No paid ads, no discounts over 10%.' },
  { value: '$$', title: 'Up to $500', text: 'Discount codes and small boosts.' },
  { value: '$$$', title: 'Up to $2,000', text: 'Adds paid local ads to the mix.' }
]

export const COMPLIANCE_RULES = {
  sms_length:
    'It runs past two text segments once the link and STOP line are added. Keep it to about 160 characters before the link.',
  review_reward: 'It offers a reward for a review, which review sites ban. Remove the offer or the review ask.'
}

export const PILL = {
  amber: 'bg-amber-100 text-amber-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-600'
}

const STATUS = {
  proposed: ['Needs your OK', PILL.amber],
  needs_edit: ['Needs an edit', PILL.red],
  approved: ['Doing it', PILL.indigo],
  scheduled: ['Scheduled', PILL.indigo],
  sending: ['Sending', PILL.blue],
  sent: ['Results in', PILL.green],
  done: ['Done', PILL.green],
  skipped: ['Skipped', PILL.gray],
  expired: ['Expired', PILL.gray],
  failed: ['Failed', PILL.red]
}
export const statusLabel = status => STATUS[status]?.[0] || status
export const statusPillClass = status => STATUS[status]?.[1] || PILL.gray

export const formatWhen = iso =>
  iso ? new Date(iso).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : ''
// A date-only string (a code expiry) is a calendar day, so it is read in UTC; a timestamp is shown in local time.
export const formatDay = iso =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(iso.length === 10 ? { timeZone: 'UTC' } : {})
      })
    : ''
export const formatReturn = x => (x === null || x === undefined ? 'n/a' : `${Number(x).toFixed(1)}x`)
export const pct = x => (x === null || x === undefined ? 'n/a' : `${(Number(x) * 100).toFixed(1)}%`)

export function deltaOf(current, prior) {
  if (!prior) return null
  const pctChange = Math.round(((current - prior) / prior) * 100)
  return { pct: Math.abs(pctChange), up: pctChange >= 0 }
}

const a = item => item.attributes || {}
const isSms = item => Boolean(a(item).sms_eligible)
const hasCode = item => Boolean(a(item).discount_code)
const hasOrders = item => (a(item).orders_count || 0) > 0

export function cardState(item) {
  const s = a(item).status
  const pill = (label, color, extra = {}) => ({ label, classes: PILL[color], dot: false, pulse: false, ...extra })
  if (s === 'proposed')
    return {
      key: isSms(item) ? 'decide' : 'decide_kit',
      hot: true,
      dim: false,
      pill: pill('Needs your OK', 'amber', { dot: true })
    }
  if (s === 'needs_edit') return { key: 'needs_edit', hot: true, dim: false, pill: pill('Needs an edit', 'red') }
  if (s === 'scheduled')
    return {
      key: 'scheduled',
      hot: false,
      dim: false,
      pill: pill(`Sends ${formatWhen(a(item).next_send_at)}`.trim(), 'indigo')
    }
  if (s === 'sending') {
    return {
      key: 'sending',
      hot: false,
      dim: false,
      pill: pill(`Sending · ${a(item).sent_count || 0} of ${a(item).recipients_count || 0}`, 'blue', { pulse: true })
    }
  }
  if (s === 'sent') return { key: 'results', hot: false, dim: false, pill: pill('Results in', 'green') }
  if (s === 'approved') {
    return hasOrders(item)
      ? { key: 'results', hot: false, dim: false, pill: pill('Results in', 'green') }
      : { key: 'doing', hot: false, dim: false, pill: pill('Doing it', 'indigo') }
  }
  if (s === 'done') {
    if (!isSms(item) && !hasCode(item))
      return { key: 'done_unmeasured', hot: false, dim: false, pill: pill('Done', 'green') }
    return { key: 'results', hot: false, dim: false, pill: pill(hasOrders(item) ? 'Results in' : 'Done', 'green') }
  }
  return { key: 'closed', hot: false, dim: true, pill: pill(statusLabel(s), s === 'failed' ? 'red' : 'gray') }
}

export function timelineFor(item) {
  const s = a(item).status
  if (isSms(item)) {
    const current = { proposed: 0, needs_edit: 0, approved: 1, scheduled: 1, sending: 2, sent: 3, done: 3 }[s] ?? 0
    const out = { steps: ['Proposed', 'Approved', 'Sent', 'Results'], current }
    return s === 'needs_edit' ? { ...out, failed: true } : out
  }
  const last = hasCode(item) ? 'Results' : 'Done'
  const current = { proposed: 0, needs_edit: 0, approved: 1, done: 2 }[s] ?? 0
  return { steps: ['Proposed', 'Doing it', last], current }
}

const ORDER = {
  proposed: 0,
  needs_edit: 1,
  scheduled: 2,
  sending: 3,
  approved: 4,
  sent: 4,
  done: 4,
  skipped: 5,
  expired: 5,
  failed: 5
}
export const sortItems = items => [...items].sort((x, y) => (ORDER[a(x).status] ?? 6) - (ORDER[a(y).status] ?? 6))

export const kitSteps = item => a(item).copy?.kit?.this_week || []

export function headerState({ agent, plan, building = false }) {
  if (!agent) return 'none'
  if (agent.attributes?.is_active === false) return 'off'
  if (agent.attributes?.pace === 'paused') return 'paused'
  if (!plan || building) return 'building'
  if (plan.attributes?.status === 'closed') return 'closed'
  const items = plan.items || []
  const decided = items.some(i => !['proposed', 'needs_edit'].includes(a(i).status))
  return decided ? 'progress' : 'ready'
}

export function nextUpSentence(plan) {
  const items = plan?.items || []
  const waiting = items.filter(i => ['proposed', 'needs_edit'].includes(a(i).status)).length
  if (waiting) return `${waiting} ${waiting === 1 ? 'play' : 'plays'} waiting on you`
  const scheduled = items.find(i => a(i).status === 'scheduled')
  if (scheduled) return `Sends ${formatWhen(a(scheduled).next_send_at)} if approved`
  const sending = items.find(i => a(i).status === 'sending')
  if (sending) return `Sending now · ${a(sending).sent_count || 0} of ${a(sending).recipients_count || 0}`
  if (items.some(i => ['sent', 'done', 'approved'].includes(a(i).status))) return 'Results update nightly'
  return ''
}

export function revenueByPlayChart(payload) {
  return {
    labels: (payload?.months || []).map(monthLabel),
    datasets: (payload?.series || []).map((s, i) => ({
      label: s.play,
      data: (s.values || []).map(c => (c || 0) / 100),
      backgroundColor: PALETTE[i % PALETTE.length],
      stack: 'revenue'
    }))
  }
}

export function funnelRows(totals) {
  const t = totals || {}
  const row = (label, value, base) => ({
    label,
    value: value || 0,
    pct: base ? Math.round(((value || 0) / base) * 100) : 0
  })
  return [
    row('Proposed', t.proposed, t.proposed),
    row('Approved', t.approved, t.proposed),
    row('Delivered', t.delivered, t.delivered),
    row('Replied', t.replied, t.delivered),
    row('Ordered', t.ordered, t.delivered)
  ]
}

export const milestoneGroups = list => ({
  earned: (list || []).filter(m => m.earned_at),
  locked: (list || []).filter(m => !m.earned_at)
})

export const laneLabel = lane => ({ online: 'Online', offline: 'Offline', retention: 'Retention' })[lane] || lane

// Mirrors Marketing::Compliance#final_sms_text and #too_long? so the editor counts what is really sent.
const SMS_STOP_LINE = ' Reply STOP to opt out.'
const SMS_PLACEHOLDER_LINK =
  'https://example-store.bottle.com/s/12345?utm_source=bottle&utm_medium=sms&utm_campaign=xxxxxxxxxxxxxxxxxxxx-2026-11'
const SMS_MAX_GSM = 306
const SMS_MAX_UNICODE = 134
const MODEL_STOP_LINE = /\s*(reply|text)\s+stop\b[^.!{]*[.!]?\s*/i
const ASCII_PUNCTUATION = {
  '\u2018': "'",
  '\u2019': "'",
  '\u201C': '"',
  '\u201D': '"',
  '\u2013': '-',
  '\u2014': '-',
  '\u2026': '...'
}
const SMS_ASCII_SAFE = /^[\x20-\x7E\n\r]*$/

export const smsFinalText = text => {
  let body = String(text || '')
    .replace(/[\u2018\u2019\u201C\u201D\u2013\u2014\u2026]/g, c => ASCII_PUNCTUATION[c])
    .replace(new RegExp(MODEL_STOP_LINE, 'gi'), ' ')
    .replace(/\s+/g, ' ')
    .trim()
  body = body.split('{link}').join(SMS_PLACEHOLDER_LINK)
  const needsPeriod = /https?:\/\/\S*[^\s.!?]$/.test(body) || !/[.!?]$/.test(body)
  return body + (needsPeriod ? '.' : '') + SMS_STOP_LINE
}

// This rule must match Marketing::Compliance (too_long? and GSM_SAFE): plain ASCII is limited to 306
// characters (two segments), anything else is UCS-2 with a limit of 134.
export function smsSegmentCount(text) {
  const final = smsFinalText(text)
  const chars = [...final].length
  const ascii = SMS_ASCII_SAFE.test(final)
  const [limit, single, multi] = ascii ? [SMS_MAX_GSM, 160, 153] : [SMS_MAX_UNICODE, 70, 67]
  return { chars, limit, segments: chars <= single ? 1 : Math.ceil(chars / multi) }
}
